import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Spin } from 'antd';
import { DownloadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { API_ORIGIN } from '../../config';

/** doAttachments holds comma-separated stored names (older rows may hold paths). */
const parseNames = (value) => {
  if (value === undefined) return null; // not included in this record: try the first file
  return String(value || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => name.split(/[\\/]/).pop());
};

const PREVIEWABLE = /^(application\/pdf|image\/)/i;

/**
 * An order's attachments. Each file is fetched with the staff login (an iframe pointed at the
 * API could not send it) and shown from a local object URL: PDFs and images inline, anything
 * else as a download.
 */
const AttachmentsViewer = ({ doId, attachments, title }) => {
  const names = useMemo(() => parseNames(attachments), [attachments]);
  const count = names === null ? 1 : names.length;
  const [index, setIndex] = useState(0);
  const [attempt, setAttempt] = useState(0);
  // { state: 'loading' | 'ready' | 'missing' | 'denied' | 'error', url, type }
  const [file, setFile] = useState({ state: 'loading' });

  const load = useCallback(
    async (signal) => {
      setFile({ state: 'loading' });
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-order/${doId}/pdf?index=${index}`, { signal });
        const type = response.headers.get('content-type') || '';
        if (response.status === 401 || response.status === 403) {
          setFile({ state: 'denied' });
          return;
        }
        if (response.status === 404 || (response.ok && /json|text\/plain|text\/html/i.test(type))) {
          setFile({ state: 'missing' });
          return;
        }
        if (!response.ok) {
          setFile({ state: 'error' });
          return;
        }
        const blob = await response.blob();
        setFile({ state: 'ready', url: URL.createObjectURL(blob), type: blob.type || type });
      } catch (error) {
        if (error.name !== 'AbortError') setFile({ state: 'error' });
      }
    },
    [doId, index]
  );

  useEffect(() => {
    if (count === 0) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, count, attempt]);

  // Release the previous file's object URL when it is replaced or the viewer closes.
  useEffect(() => () => file.url && URL.revokeObjectURL(file.url), [file.url]);

  if (count === 0) {
    return (
      <div className="do-empty">
        <strong>No attachments</strong>
        No files have been uploaded for this order.
      </div>
    );
  }

  const label = (i) => (names && names[i] ? names[i] : `Attachment ${i + 1}`);

  let body;
  if (file.state === 'loading') {
    body = (
      <div className="do-empty" role="status">
        <Spin size="small" /> <span style={{ marginLeft: 8 }}>Loading attachment</span>
      </div>
    );
  } else if (file.state === 'missing') {
    body = (
      <div className="do-empty">
        <strong>File not found</strong>
        This attachment is no longer on the server. It can be uploaded again from the order.
      </div>
    );
  } else if (file.state === 'denied') {
    body = (
      <div className="do-empty">
        <strong>No access</strong>
        Only the requester, people copied in and order admins can open this order's files.
      </div>
    );
  } else if (file.state === 'error') {
    body = (
      <div className="do-empty">
        <strong>The attachment could not be loaded</strong>
        <span style={{ display: 'block', marginBottom: 16 }}>Check your connection and try again.</span>
        <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
      </div>
    );
  } else if (PREVIEWABLE.test(file.type)) {
    body = file.type.startsWith('image/') ? (
      <img src={file.url} alt={label(index)} style={{ display: 'block', maxWidth: '100%', borderRadius: 8, border: '1px solid var(--do-line)' }} />
    ) : (
      <iframe
        src={file.url}
        title={`${title}: ${label(index)}`}
        style={{ width: '100%', height: 640, border: '1px solid var(--do-line)', borderRadius: 8 }}
      />
    );
  } else {
    body = (
      <div className="do-empty">
        <strong>{label(index)}</strong>
        <span style={{ display: 'block', marginBottom: 16 }}>This file type can't be previewed here.</span>
        <Button icon={<DownloadOutlined />} href={file.url} download={label(index)}>
          Download
        </Button>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {count > 1 &&
          Array.from({ length: count }, (_, i) => (
            <Button
              key={i}
              size="small"
              type={i === index ? 'primary' : 'default'}
              icon={<PaperClipOutlined />}
              onClick={() => setIndex(i)}
            >
              {label(i)}
            </Button>
          ))}
        <span style={{ flex: 1 }} />
        {file.state === 'ready' && (
          <a className="do-link" href={file.url} target="_blank" rel="noopener noreferrer">
            Open in new tab
          </a>
        )}
      </div>
      {body}
    </>
  );
};

export default AttachmentsViewer;
